import type { LottieJson, LottieLayer, LottieShape } from './types';

const REQUIRED: Array<keyof LottieJson> = ['v', 'fr', 'ip', 'op', 'w', 'h', 'layers'];
/** Geometry/paint primitives that must live inside a gr group's it array. */
const PRIMITIVES = new Set(['el', 'rc', 'sh', 'sr', 'fl', 'st', 'gf', 'gs']);

function checkGroups(shapes: LottieShape[] | undefined, layerName: string, errs: string[]): void {
  if (!shapes) return;
  for (const s of shapes) {
    if (PRIMITIVES.has(s.ty)) {
      errs.push(`layer "${layerName}": shape "${s.ty}" is not wrapped in a "gr" group — Skottie renders flat shape lists blank`);
    } else if (s.ty === 'gr') {
      const it = s.it ?? [];
      if (it.length === 0 || it[it.length - 1].ty !== 'tr') {
        errs.push(`layer "${layerName}": a "gr" group must end with a "tr" transform`);
      }
      checkColorRange(it, layerName, errs);
    }
  }
}

function checkColorRange(it: LottieShape[], layerName: string, errs: string[]): void {
  for (const s of it) {
    if ((s.ty === 'fl' || s.ty === 'st') && s.c && typeof s.c === 'object') {
      const k = (s.c as { k?: unknown }).k;
      if (Array.isArray(k) && k.some((v) => typeof v === 'number' && v > 1)) {
        errs.push(`layer "${layerName}": "${s.ty}" color components must be 0–1 RGBA, not 0–255`);
      }
    }
  }
}

/** Mechanized authoring checklist. [] = renderable; otherwise human-readable errors. */
export function validateLottie(doc: unknown): string[] {
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) {
    return ['not a JSON object — a Lottie document is a single top-level object'];
  }
  const d = doc as Partial<LottieJson>;
  const errs: string[] = [];
  for (const f of REQUIRED) {
    if (d[f] === undefined) errs.push(`missing required top-level field "${String(f)}"`);
  }
  if (!Array.isArray(d.layers)) {
    if (d.layers !== undefined) errs.push('"layers" must be an array');
    return errs;
  }
  const op = typeof d.op === 'number' ? d.op : 0;
  for (const layer of d.layers as LottieLayer[]) {
    const name = layer.nm ?? `ty${layer.ty}`;
    if (typeof layer.op === 'number' && layer.op < op) {
      errs.push(`layer "${name}": op=${layer.op} ends before the document op=${op} — it will vanish early`);
    }
    checkGroups(layer.shapes, name, errs);
  }
  return errs;
}
